import { Link, useSearchParams } from 'react-router-dom'
import { Trans } from 'react-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import {
  LoginPayload,
  useCheckSsoAvailability,
  useRequestMagicLink
} from '@/features/auth/hooks/auth'
import { AxiosError } from 'axios'
import { Alert, Button, InputGroup } from '@/common'
import AuthLayout from './components/AuthLayout'
import EmailInputGroup from '@/shared/components/EmailInputGroup'
import { apiClient } from '@/api/apiClient'
import useI18n from '@/shared/hooks/usei18n'

const MAGIC_LINK_RESEND_COOLDOWN_SEC = 60

type CredentialStep = 'password' | 'magicLinkRequest' | 'magicLinkSent'

export default function LoginWithSso({
  login,
  isLoggingIn
}: {
  login: (payload: LoginPayload) => void
  isLoggingIn: boolean
}) {
  const { t } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [serverError, setServerError] = useState<string | null>(null)
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [step, setStep] = useState<1 | 2>(1)
  const [credentialStep, setCredentialStep] = useState<CredentialStep>('password')
  const [resendCooldownSec, setResendCooldownSec] = useState(0)
  const magicLinkResendTimerRef = useRef<number | null>(null)

  const clearMagicLinkResendTimer = useCallback(() => {
    if (magicLinkResendTimerRef.current !== null) {
      window.clearInterval(magicLinkResendTimerRef.current)
      magicLinkResendTimerRef.current = null
    }
  }, [])

  const startMagicLinkResendCooldown = useCallback(() => {
    clearMagicLinkResendTimer()
    setResendCooldownSec(MAGIC_LINK_RESEND_COOLDOWN_SEC)
    const timer = window.setInterval(() => {
      setResendCooldownSec(prev => {
        if (prev <= 1) {
          window.clearInterval(timer)
          magicLinkResendTimerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
    magicLinkResendTimerRef.current = timer
  }, [clearMagicLinkResendTimer])

  const {
    mutateAsync: checkSsoAvailability,
    data: ssoAvailabilityData,
    isPending: isCheckingSso
  } = useCheckSsoAvailability()
  const { mutateAsync: requestMagicLink, isPending: isSendingMagicLink } = useRequestMagicLink()

  const isSsoAvailable = ssoAvailabilityData?.available || false
  const organizationId = ssoAvailabilityData?.organizationId || null

  useEffect(() => {
    if (step !== 1) {
      return
    }
    clearMagicLinkResendTimer()
    setCredentialStep('password')
    setResendCooldownSec(0)
    setMagicLinkError(null)
  }, [step, clearMagicLinkResendTimer])

  useEffect(() => () => clearMagicLinkResendTimer(), [clearMagicLinkResendTimer])

  useEffect(() => {
    if (searchParams.get('error') === 'ORGANIZATION_DEACTIVATED') {
      setServerError('login.errors.organizationDeactivated')
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const onLogin = useCallback(
    async (payload: LoginPayload) => {
      setServerError(null)
      try {
        return await login(payload)
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 401) {
          if (err.response.data?.message === 'ORGANIZATION_DEACTIVATED') {
            setServerError('login.errors.organizationDeactivated')
            return
          }
          setServerError('login.errors.unauthorized')
        } else {
          setServerError('login.errors.general')
        }
      }
    },
    [login]
  )

  const handleContinue = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()
      const emailSchema = z.email()
      const result = emailSchema.safeParse(email)
      if (!result.success) {
        return
      }

      setServerError(null)
      try {
        await checkSsoAvailability(email)
        setCredentialStep('password')
        setStep(2)
      } catch {
        setServerError('login.errors.general')
      }
    },
    [email, checkSsoAvailability]
  )

  const handleSsoLogin = useCallback(() => {
    if (!organizationId) {
      return
    }

    const ssoInitUrl = `${apiClient.defaults.baseURL}/v1/auth/sso/init?organizationId=${organizationId}`
    window.location.href = ssoInitUrl
  }, [organizationId])

  const handlePasswordLogin = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()
      await onLogin({ email, password })
    },
    [email, password, onLogin]
  )

  const sendMagicLink = useCallback(async () => {
    setMagicLinkError(null)
    try {
      await requestMagicLink({ email })
      setCredentialStep('magicLinkSent')
      startMagicLinkResendCooldown()
    } catch {
      setMagicLinkError('login.magicLink.errors.general')
    }
  }, [email, requestMagicLink, startMagicLinkResendCooldown])

  return (
    <AuthLayout>
      <div className="flex flex-col items-center space-y-4 w-full max-w-md">
        {serverError && (
          <Alert
            type="error"
            className={
              serverError === 'login.errors.organizationDeactivated'
                ? 'whitespace-pre-line w-full'
                : 'w-full'
            }
          >
            {t(serverError)}
          </Alert>
        )}
        {step === 1 && (
          <>
            <form onSubmit={handleContinue} className="space-y-4 w-full">
              <EmailInputGroup
                onChange={evt => setEmail(evt.target.value)}
                value={email}
                name="email"
                label={t('loginForm.fields.email.label')}
              />

              <Button
                type="submit"
                className="w-full"
                variant="default"
                disabled={isCheckingSso || !email}
                isLoading={isCheckingSso}
              >
                {t('login.continue', { defaultValue: 'Continue' })}
              </Button>
              <div className="text-sm px-10 text-center">
                <Trans
                  i18nKey="login.dontHaveAnAccount"
                  components={{
                    Link: <Link className="text-green-600 underline" to="/signup" />
                  }}
                />
              </div>
            </form>
          </>
        )}
        {step === 2 && (
          <div className="space-y-4 w-full">
            <EmailInputGroup
              onChange={evt => setEmail(evt.target.value)}
              value={email}
              disabled={step === 2}
              name="email"
              label={t('loginForm.fields.email.label')}
            />
            {isSsoAvailable && organizationId ? (
              <>
                <Button onClick={handleSsoLogin} className="w-full" variant="default">
                  {t('login.sso.signInWithSso', { defaultValue: 'Sign in with SSO' })}
                </Button>
                <div className="text-sm px-10 text-center">
                  <button
                    type="button"
                    className="text-green-600 underline"
                    onClick={() => setStep(1)}
                  >
                    {t('login.backToEmail')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {credentialStep === 'password' && (
                  <>
                    <form onSubmit={handlePasswordLogin} className="space-y-4">
                      <InputGroup
                        onChange={evt => setPassword(evt.target.value)}
                        value={password}
                        name="password"
                        label={t('loginForm.fields.password.label')}
                        type="password"
                        hint={
                          <Link className="text-green-600 underline" to="/forgot-password">
                            {t('loginForm.fields.password.forgotPassword')}
                          </Link>
                        }
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        variant="default"
                        disabled={isLoggingIn || password.trim().length === 0}
                        isLoading={isLoggingIn}
                      >
                        {t('loginForm.actions.login')}
                      </Button>
                    </form>
                    <p className="text-center text-sm text-gray-600">{t('login.orDivider')}</p>
                    <Button
                      type="button"
                      className="w-full"
                      variant="subtle"
                      onClick={() => {
                        setMagicLinkError(null)
                        setCredentialStep('magicLinkRequest')
                      }}
                    >
                      {t('login.magicLink.logInWithMagicLink')}
                    </Button>
                    <div className="text-sm px-10 text-center">
                      <button
                        type="button"
                        className="text-green-600 underline"
                        onClick={() => setStep(1)}
                      >
                        {t('login.backToEmail')}
                      </button>
                    </div>
                  </>
                )}

                {credentialStep === 'magicLinkRequest' && (
                  <>
                    <p className="text-sm text-center text-gray-600">
                      {t('login.magicLink.request.description')}
                    </p>
                    <Button
                      type="button"
                      className="w-full"
                      variant="default"
                      onClick={() => void sendMagicLink()}
                      disabled={isSendingMagicLink}
                      isLoading={isSendingMagicLink}
                    >
                      {t('login.magicLink.request.sendAction')}
                    </Button>
                    {magicLinkError && <Alert type="error">{t(magicLinkError)}</Alert>}
                    <div className="text-sm px-10 text-center space-y-2 flex flex-col items-center">
                      <button
                        type="button"
                        className="text-green-600 underline"
                        onClick={() => {
                          setMagicLinkError(null)
                          setCredentialStep('password')
                        }}
                      >
                        {t('login.magicLink.request.backToPassword')}
                      </button>
                      <button
                        type="button"
                        className="text-green-600 underline"
                        onClick={() => setStep(1)}
                      >
                        {t('login.backToEmail')}
                      </button>
                    </div>
                  </>
                )}

                {credentialStep === 'magicLinkSent' && (
                  <>
                    <Alert type="success" className="whitespace-pre-line">
                      {t('login.magicLink.sent.detail', { email })}
                    </Alert>
                    <Button
                      type="button"
                      className="w-full"
                      variant="default"
                      onClick={() => void sendMagicLink()}
                      disabled={isSendingMagicLink || resendCooldownSec > 0}
                      isLoading={isSendingMagicLink && resendCooldownSec === 0}
                    >
                      {resendCooldownSec > 0
                        ? t('login.magicLink.sent.resendWait', { seconds: resendCooldownSec })
                        : t('login.magicLink.sent.resend')}
                    </Button>
                    {magicLinkError && <Alert type="error">{t(magicLinkError)}</Alert>}
                    <div className="text-sm px-10 text-center space-y-2 flex flex-col items-center">
                      <button
                        type="button"
                        className="text-green-600 underline"
                        onClick={() => {
                          setMagicLinkError(null)
                          setCredentialStep('password')
                        }}
                      >
                        {t('login.magicLink.sent.backToPassword')}
                      </button>
                      <button
                        type="button"
                        className="text-green-600 underline"
                        onClick={() => setStep(1)}
                      >
                        {t('login.magicLink.sent.useDifferentEmail')}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </AuthLayout>
  )
}
